import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Workspace, AgentTemplate } from '../types';
import { listWorkspaces, deleteWorkspace, listTemplates } from '../api/client';

export function WorkspaceSetup() {
  const navigate = useNavigate();
  const [workspaces, setWorkspaces] = useState<Workspace[]>([]);
  const [templates, setTemplates] = useState<AgentTemplate[]>([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const [w, t] = await Promise.all([listWorkspaces(), listTemplates()]);
      setWorkspaces(w);
      setTemplates(t);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleDelete = async (id: string) => {
    if (!window.confirm('Delete this workspace?')) return;
    await deleteWorkspace(id);
    await load();
  };

  const copy = (text: string) => navigator.clipboard.writeText(text);

  return (
    <div style={s.container}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h2 style={s.heading}>Workspaces</h2>
        <button style={s.btn} onClick={() => navigate('/admin/workspaces/new')}>+ New Workspace</button>
      </div>

      {loading ? <div style={s.muted}>Loading...</div> : workspaces.length === 0 ? <div style={s.muted}>No workspaces yet</div> : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {workspaces.map((workspace) => (
            <div key={workspace.id} style={s.card} onClick={() => navigate(`/admin/workspaces/${workspace.id}`)}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <strong>{workspace.name}</strong>
                <span style={{ fontSize: 12, color: '#888' }}>{workspace.templateIds.length} templates</span>
              </div>
              <div style={s.keyRow}>
                <code style={s.apiKey}>{workspace.apiKey}</code>
                <button style={s.copyBtn} onClick={(e) => { e.stopPropagation(); copy(workspace.apiKey); }}>Copy</button>
              </div>
              <div style={{ fontSize: 12, color: '#888', marginTop: 4 }}>
                Templates: {workspace.templateIds.length > 0
                  ? workspace.templateIds.map((id) => templates.find((t) => t.id === id)?.name || id.slice(0, 8)).join(', ')
                  : 'none'}
              </div>
              <button style={s.delBtn} onClick={(e) => { e.stopPropagation(); handleDelete(workspace.id); }}>Delete</button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const s: Record<string, React.CSSProperties> = {
  container: { padding: 20, flex: 1, overflowY: 'auto' },
  heading: { margin: 0, fontSize: 18, color: '#1a1a1a' },
  btn: { padding: '8px 16px', borderRadius: 8, border: 'none', background: '#4f46e5', color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer' },
  delBtn: { padding: '4px 12px', borderRadius: 6, border: '1px solid #fca5a5', background: '#fff', color: '#dc2626', fontSize: 12, cursor: 'pointer', marginTop: 8 },
  muted: { color: '#888', fontSize: 14 },
  card: { padding: '12px 16px', border: '1px solid #e5e7eb', borderRadius: 8, background: '#fafafa', cursor: 'pointer' },
  keyRow: { display: 'flex', gap: 8, alignItems: 'center', marginTop: 6 },
  apiKey: { flex: 1, fontSize: 11, fontFamily: 'monospace', color: '#666', background: '#e2e8f0', padding: '4px 8px', borderRadius: 4 },
  copyBtn: { padding: '3px 8px', borderRadius: 6, border: '1px solid #ddd', background: '#fff', fontSize: 11, cursor: 'pointer', color: '#555' },
};
